<template lang="pug">
  .layout
    y-dialog(v-if="showModal" v-model='showModal' scrollable max-width='600px' persistent)
      v-card
        v-card-title.px-4
          v-layout
            div.h6.basic800--text {{ $t('variant_images') }}

        v-divider.basic300
        v-card-text.px-4.pb-0
          v-layout.my-6(v-if="isFetching")
            v-row.fill-height.ma-0(align='center' justify='center')
              v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
          .contain-layout(v-else)
            .items-layout.pt-4
              v-row.d-flex.align-end(no-gutters)
                template(v-for='(image, idx) in images')
                  v-col(cols="4" style="padding: 1px")
                    v-sheet.badge-position.clickable.d-flex.align-center.justify-center(class="rounded-circle" color="basic500" height="2.5vh" width="2.5vh" @click="removeImage(image)")
                      v-icon(:small="$vuetify.breakpoint.smAndUp" :x-small="$vuetify.breakpoint.xsOnly" color="white") mdi-close
                    v-card.ma-1(flat)
                      v-img(:src="typeof image.id === 'number' ? image.file : image.fileUrl" aspect-ratio="1")
                        template(v-slot:placeholder)
                          v-row(class="fill-height ma-0" align="center" justify="center")
                            v-progress-circular(indeterminate color="grey")
                v-col(cols="4" style="padding: 1px")
                  v-card.ma-1(outlined style="border: 1px solid" @click="$refs.file.click()")
                    v-card-text.pa-4.d-flex.justify-center.align-center(:style="plusImageHeight")
                      v-icon mdi-plus
                input.input-layout(ref="file" type="file" @change="onFileChange" accept="image/*" style="width: 60px !important" multiple="multiple")
          
        v-card-actions
          v-spacer
          v-btn.ma-0(color='white', text @click='showModal = false')
            .button-Aa-small.info500--text {{ $t('close') }}
    
    .modals
      y-dialog(v-if="openDeleteDialog" v-model='openDeleteDialog', max-width='320')
        v-card
          v-card-title.h6.basic800--text.px-4 {{ $t('delete_image') }}
          v-card-text.p2.primary500--text.px-4
            div(v-if="images.filter(f => f.selected === true).length > 1") {{ $t('delete_images_confirmation') }}
            div(v-else) {{ $t('delete_image_confirmation') }}
          v-card-actions
            v-spacer
            v-btn(color='white', text @click='openDeleteDialog = false')
              .button-Aa-small.primary400--text {{ $t('cancel') }}
            v-btn.mr-2(color='danger500' depressed height=32 @click='startDelete' :loading="isLoading")
              .button-AA-small.basic100--text {{ $t('delete') }}

</template>

<script>
export default {
  name: 'ManageProductVariantImagesDialog',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    parentProduct: {
      type: Object,
      default: () => {
        return {}
      }
    },
    parentSpec: {
      type: Object,
      default: () => {
        return {}
      }
    },
    tempOptionImages: {
      type: Object,
      default: () => {
        return {}
      }
    },
    previewImages: {
      type: Array,
      default: () => {
        return []
      }
    },
  },
  data: () => ({
    isLoading: false,
    isFetching: false,
    openDeleteDialog: false,
    parentImages: []
  }),
  mounted() {
    this.fetchImages()
  },
  computed: {
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    plusImageHeight() {
      if (this.$vuetify.breakpoint.xs) {
        return 'min-height: 80px'
      } else {
        return 'min-height: 170px'
      }
    },
    images() {
      const filterUploads = this.tempOptionImages.uploading.filter(image => image.specId === this.parentSpec.id)
      let uploadingImages = []
      if (filterUploads.length > 0) {
        uploadingImages = [...filterUploads[0].images]
      }
      const filterDelete = this.tempOptionImages.deleting.filter(image => image.specId === this.parentSpec.id)
      let deletingImages = []
      if (filterDelete.length > 0) {
        deletingImages = [...filterDelete[0].images]
      }
      return this.parentImages.filter(image => !deletingImages.includes(image.id)).concat(uploadingImages)
    },
  },
  methods: {
    fetchImages() {
      // Dont fetch if new spec
      if (typeof this.parentSpec.id === 'string') {
        this.$emit('updateParentSpecImages', { specId: this.parentSpec.id, images: [] })
        return
      }
      // Don't refetch
      if (this.parentSpec.images) {
        this.parentImages = this.parentSpec.images
        return
      }
      this.isFetching = true
      // fetching
      this.axios
        .get(`/products/${this.parentProduct.id}/mapper_images/${this.parentSpec.id}`)
        .then(resp => {
          this.parentImages = resp.data
          this.$emit('updateParentSpecImages', { specId: this.parentSpec.id, images: resp.data })
        })
        .catch(err => {
          console.log(err)
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    removeImage(image) {
      if (typeof image.id === 'number') {
        const foundIndex = this.tempOptionImages.deleting.findIndex(imageGroup => imageGroup.specId === this.parentSpec.id)
        if (foundIndex < 0) {
          this.tempOptionImages.deleting.push({
            specId: this.parentSpec.id,
            images: [image.id]
          })
        } else {
          this.tempOptionImages.deleting[foundIndex].images.push(image.id)
        }
      } else {
        this.tempOptionImages.uploading.forEach(group => {
          if (group.specId === this.parentSpec.id) {
            group.images = group.images.filter(groupImage => groupImage.id !== image.id)
          }
        })
      }
    },
    onFileChange(e) {
      let files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      let file = files[0]
      let fileReader = new FileReader()
      fileReader.onload = e => {
        let image = new Image()
        image.onload = () => {
          let canvas = document.createElement('canvas')
          let maxSize = 960
          let width = image.width
          let height = image.height
          let resizeFactor = 1.0
          if (width > maxSize || height > maxSize) {
            if (height > width) {
              resizeFactor = maxSize / height
            } else {
              resizeFactor = maxSize / width
            }
          }
          let resizeWidth = width * resizeFactor
          let resizeHeight = height * resizeFactor
          canvas.width = resizeWidth
          canvas.height = resizeHeight
          canvas.getContext('2d').drawImage(image, 0, 0, resizeWidth, resizeHeight)
          // create new data
          const id = `tmp_${+new Date()}`
          const fileUrl = canvas.toDataURL('image/jpeg')
          canvas.toBlob(
            blob => {
              const foundIndex = this.tempOptionImages.uploading.findIndex(imageGroup => imageGroup.specId === this.parentSpec.id)
              if (foundIndex < 0) {
                this.tempOptionImages.uploading.push({
                  specId: this.parentSpec.id,
                  images: [
                    {
                      id,
                      fileUrl,
                      file: new File([blob], 'product_image.jpeg', {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                      })
                    }
                  ]
                })
              } else {
                this.tempOptionImages.uploading[foundIndex].images.push({
                  id,
                  fileUrl,
                  file: new File([blob], 'product_image.jpeg', {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  })
                })
              }
            },
            'image/jpeg',
            1
          )
        }
        image.src = e.target.result
      }
      fileReader.readAsDataURL(file)
    }
  }
}
</script>

<style scoped>
.file-layout {
  position: relative;
}
.badge-position {
  margin-left: auto;
  position: relative;
  top: 14px;
  right: 2px;
  z-index: 2;
}
.input-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.contain-layout {
  min-height: 150px;
}
</style>

<i18n>
{
  "en": {
    "variant_images": "Variant images",
    "delete_images_confirmation": "Are you sure you want to delete these images? These images will be deleted permanently."
  },
  "id": {
    "variant_images": "Gambar Varian",
    "delete_images_confirmation": "Anda yakin ingin menghapus gambar ini? Gambar-gambar ini akan dihapus secara permanen."
  }
}
</i18n>
